/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// constants
const CANVAS_SIZE_DEFAULT = Number("100");
const MATH_HALF = Number("0.5");
const MATH_PI_2 = Math.PI * Number("2");
const SHAPE_SIZE_DEFAULT = Number("10");

class CanvasUtils {

  /**
  * @param {Number} width
  * @param {Number} height
  * @returns {HTMLCanvasElement}
  */
  static create(width, height) {
    const canvas = window.document.createElement("canvas");
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const scale = window.devicePixelRatio || Number("1");
    const originalHeight = Number(height) || Number(CANVAS_SIZE_DEFAULT);
    const originalWidth = Number(width) || Number(CANVAS_SIZE_DEFAULT);
    canvas.height = Math.floor(originalHeight * scale);
    canvas.width = Math.floor(originalWidth * scale);
    ctx.scale(scale, scale);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    return canvas;
  }

  /**
  * @param {Number} width
  * @param {Number} height
  * @returns {HTMLCanvasElement}
  */
  static createUnscaled(width, height) {
    const canvas = window.document.createElement("canvas");
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    canvas.height = Number(height);
    canvas.width = Number(width);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    return canvas;
  }

  /**
  * @param {HTMLCanvasElement} width
  */
  static clean(canvas) {
    canvas.remove();
  }

  /**
  * @param {HTMLCanvasElement} canvas
  * @param {Boolean} clip
  * @param {String} fillStyle
  * @param {Number} lineWidth
  * @param {Number} radius
  * @param {String} strokeStyle
  * @param {Number} xOffset
  * @param {Number} yOffset
  */
  static drawCircle(canvas, {
    clip = false,
    fillStyle = "none",
    lineWidth = 0,
    radius = SHAPE_SIZE_DEFAULT,
    strokeStyle = "none",
    xOffset = 0,
    yOffset = 0
  } = {}) {

    // context
    const ctx = canvas.getContext("2d");
    ctx.beginPath();

    // styles
    ctx.fillStyle = fillStyle;
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = strokeStyle;

    // path
    ctx.arc(xOffset, yOffset, radius, 0, MATH_PI_2, true);

    // clip, fill, & stroke
    if(clip) ctx.clip();
    if(fillStyle !== "none") ctx.fill();
    if(strokeStyle !== "none") ctx.stroke();
  }

  /**
  * @param {HTMLCanvasElement} canvas
  * @param {Number} clipHeight
  * @param {Number} clipWidth
  * @param {Number} clipXOffset
  * @param {Number} clipYOffset
  * @param {Number} imageHeight
  * @param {Number} imageWidth
  * @param {Number} imageXOffset
  * @param {Number} imageYOffset
  */
  static drawImage(canvas, image, {
    clipHeight = Number("100"),
    clipWidth = Number("100"),
    clipXOffset = Number("0"),
    clipYOffset = Number("0"),
    imageHeight = Number("100"),
    imageWidth = Number("100"),
    imageXOffset = Number("0"),
    imageYOffset = Number("0")
  }) {

    // context
    const ctx = canvas.getContext("2d");
    ctx.beginPath();

    // image
    ctx.drawImage(
      image,
      clipXOffset,
      clipYOffset,
      clipWidth,
      clipHeight,
      imageXOffset,
      imageYOffset,
      imageWidth,
      imageHeight
    );
  }

  /**
  * @param {HTMLCanvasElement} canvas
  * @param {Boolean} clip
  * @param {String} fillStyle
  * @param {Number} height
  * @param {Number} lineWidth
  * @param {Number} radiusBottomLeft
  * @param {Number} radiusBottomRight
  * @param {Number} radiusTopLeft
  * @param {Number} radiusTopRight
  * @param {String} strokeStyle
  * @param {Number} width
  * @param {Number} xOffset
  * @param {Number} yOffset
  */
  static drawSquare(canvas, {
    clip = false,
    fillStyle = "none",
    height = SHAPE_SIZE_DEFAULT,
    lineWidth = Number("0"),
    radiusBottomLeft = Number("0"),
    radiusBottomRight = Number("0"),
    radiusTopLeft = Number("0"),
    radiusTopRight = Number("0"),
    strokeStyle = "none",
    width = SHAPE_SIZE_DEFAULT,
    xOffset = Number("0"),
    yOffset = Number("0")
  } = {}) {

    // context
    const ctx = canvas.getContext("2d");
    ctx.beginPath();

    // styles
    ctx.fillStyle = fillStyle;
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = strokeStyle;

    // stroke offsets
    let finalHeight = height;
    let finalWidth = width;
    let finalX = xOffset;
    let finalY = yOffset;
    if(strokeStyle !== "none") {
      const halfLineWIdth = lineWidth * MATH_HALF;
      finalHeight = height - lineWidth;
      finalWidth = width - lineWidth;
      finalX = xOffset + halfLineWIdth;
      finalY = yOffset + halfLineWIdth;
    }

    // path - rounded corners
    const hasRoundedCorners = (
      radiusBottomLeft +
      radiusBottomRight +
      radiusTopLeft +
      radiusTopRight
    ) !== 0;
    if(hasRoundedCorners) {
      const topCenter = finalX + (width * MATH_HALF);
      ctx.moveTo(topCenter, finalY);
      ctx.arcTo(finalX + finalWidth, finalY, finalX + finalWidth, finalY + finalHeight, radiusTopRight);
      ctx.arcTo(finalX + finalWidth, finalY + finalHeight, finalX, finalY + finalHeight, radiusBottomRight);
      ctx.arcTo(finalX, finalY + finalHeight, finalX, finalY, radiusBottomLeft);
      ctx.arcTo(finalX, finalY, finalX + finalWidth, finalY, radiusTopLeft);
      ctx.lineTo(topCenter, finalY);
    }

    // path - straight corners
    else {
      ctx.rect(finalX, finalY, finalWidth, finalHeight);
    }

    // clip, fill, & stroke
    if(clip) ctx.clip();
    if(fillStyle !== "none") ctx.fill();
    if(strokeStyle !== "none") ctx.stroke();
  }
}
export default CanvasUtils;
